import type { Ecriture } from '@shared/types/Ecriture';
import { roundFCFA } from '@shared/utils/round';
import { COMPTES_TVA_PAR_DEFAUT, roleCompteTva, type TableComptesTva } from './ComptesTva';

export interface DeclarationTVA {
  periode: string; // "YYYY-MM"
  tva_collectee: number; // crédit 442 (référentiel) — alias 4431 / 4457
  tva_deductible_biens: number; // débit 444 — alias 4452 / 4456
  tva_deductible_immo: number; // débit 4451
  credit_anterieur: number;
  tva_due: number; // collectée - déductibles - crédit
  credit_reportable: number;
}

/**
 * Comptes TVA SYSCOHADA révisé (Gabon / OHADA AUDCIF).
 * Les comptes 4456/4457 (PCG français) sont acceptés en lecture seule (données historiques).
 */
export const COMPTES_TVA_OHADA = {
  collectee: '442',
  collectee_alias: '4431',
  deductible_biens: '444',
  deductible_biens_alias: '4452',
  deductible_immo: '4451',
} as const;

/**
 * Déclaration TVA mensuelle — CGI Gabon Livre I Titre II.
 * Échéance : 15 du mois suivant. Taux normal 18 % (TAUX_TVA).
 */
export class DeclarationTVAService {
  static generer(
    ecritures: readonly Ecriture[],
    periode: string,
    credit_anterieur: number = 0,
    comptes: TableComptesTva = COMPTES_TVA_PAR_DEFAUT,
  ): DeclarationTVA {
    let tva_collectee = 0;
    let tva_deductible_biens = 0;
    let tva_deductible_immo = 0;

    for (const e of ecritures) {
      if (e.periode !== periode) continue;
      if (e.source === 'cloture') continue;
      /** « due » et « crédit » sont des comptes de liquidation : ignorés, jamais lus comme collectée/déductible. */
      const role = roleCompteTva(e.compte, comptes);
      if (role === 'collectee') tva_collectee += e.credit - e.debit;
      else if (role === 'deductible_immo') tva_deductible_immo += e.debit - e.credit;
      else if (role === 'deductible_biens') tva_deductible_biens += e.debit - e.credit;
    }

    tva_collectee = roundFCFA(tva_collectee);
    tva_deductible_biens = roundFCFA(tva_deductible_biens);
    tva_deductible_immo = roundFCFA(tva_deductible_immo);
    const credit = roundFCFA(Math.max(0, credit_anterieur));
    const total_deductible = tva_deductible_biens + tva_deductible_immo;
    const solde = tva_collectee - total_deductible - credit;

    return {
      periode,
      tva_collectee,
      tva_deductible_biens,
      tva_deductible_immo,
      credit_anterieur: credit,
      tva_due: solde > 0 ? solde : 0,
      credit_reportable: solde < 0 ? -solde : 0,
    };
  }

  /** Crédit N-1 en chaînant les mois antérieurs (ou le registre persisté). */
  static creditAnterieur(
    ecritures: readonly Ecriture[],
    periode: string,
    stored: readonly { periode: string; credit: number }[] = [],
    comptes: TableComptesTva = COMPTES_TVA_PAR_DEFAUT,
  ): number {
    const [y, m] = periode.split('-').map(Number);
    const prevDate = new Date(y ?? 2026, (m ?? 1) - 2, 1);
    const prev = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
    const deja = stored.find((c) => c.periode === prev);
    if (deja) return deja.credit;

    const periodes = [...new Set(ecritures.map((e) => e.periode).filter(Boolean))]
      .filter((p) => p < periode)
      .sort();
    if (periodes.length === 0) return 0;

    let credit = 0;
    let cursor = periodes[0]!;
    while (cursor <= prev) {
      const known = stored.find((c) => c.periode === cursor);
      credit = known ? known.credit : this.generer(ecritures, cursor, credit, comptes).credit_reportable;
      const [cy, cm] = cursor.split('-').map(Number);
      const next = new Date(cy ?? 2026, cm ?? 1, 1);
      cursor = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
    }
    return credit;
  }
}
